/**
 * Auth API Contracts
 *
 * Base Zod schemas for authentication endpoints.
 * Backend imports these and adds .openapi() metadata.
 */

import { z } from 'zod'

// =============================================================================
// GET /v1/auth/me - Current User Profile
// =============================================================================

/**
 * School subscription plan details
 */
export const SubscriptionPlanSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  features: z.array(z.string())
})

/**
 * School information within a connection
 */
export const SchoolInfoSchema = z.object({
  id: z.string(),
  platform: z.string().nullable(),
  name: z.string().nullable(),
  environmentName: z.string().nullable(),
  slug: z.string().nullable(),
  status: z.string(),
  subscriptionPlan: SubscriptionPlanSchema.nullable()
})

/**
 * User information at a specific school
 */
export const SchoolUserInfoSchema = z.object({
  schoolUserId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  email: z.null(),
  image: z.null(),
  isStudent: z.boolean(),
  isParent: z.boolean(),
  isFaculty: z.boolean(),
  isAdmin: z.boolean(),
  isBetaTester: z.boolean(),
  hasSkyApiSyncRole: z.boolean(),
  status: z.string()
})

/**
 * School connection - combines user info with school info
 */
export const SchoolConnectionSchema = z.object({
  id: z.string(),
  user: SchoolUserInfoSchema,
  school: SchoolInfoSchema
})

/**
 * Last logged in school (simplified)
 */
export const LastLoggedInSchoolSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable(),
    slug: z.string().nullable()
  })
  .nullable()

/**
 * Current user profile - response from GET /v1/auth/me
 * Note: Returns directly (not wrapped in message/data envelope)
 */
export const CurrentUserProfileSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  nickname: z.string().nullable(),
  image: z.string().nullable(),
  isAdmin: z.boolean(),
  lastLoggedInSchool: LastLoggedInSchoolSchema,
  schoolConnections: z.array(SchoolConnectionSchema)
})

export const GetMeResponseSchema = CurrentUserProfileSchema

export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>
export type SchoolInfo = z.infer<typeof SchoolInfoSchema>
export type SchoolUserInfo = z.infer<typeof SchoolUserInfoSchema>
export type SchoolConnection = z.infer<typeof SchoolConnectionSchema>
export type LastLoggedInSchool = z.infer<typeof LastLoggedInSchoolSchema>
export type CurrentUserProfile = z.infer<typeof CurrentUserProfileSchema>
export type GetMeResponse = z.infer<typeof GetMeResponseSchema>

// =============================================================================
// POST /v1/auth/actor-token/:userId - Impersonation
// =============================================================================

export const ActorTokenRequestSchema = z.object({
  target_school_user_public_id: z.string().min(1)
})

/**
 * Clerk actor token object
 */
export const ClerkActorTokenSchema = z.object({
  object: z.string(),
  id: z.string(),
  status: z.enum(['pending', 'accepted', 'revoked']),
  user_id: z.string(),
  actor: z.object({}),
  token: z.string(),
  url: z.string(),
  created_at: z.number(),
  updated_at: z.number()
})

/**
 * Roles object for school user
 */
export const SchoolUserRolesSchema = z.object({
  isStudent: z.boolean().optional(),
  isParent: z.boolean().optional(),
  isFaculty: z.boolean().optional(),
  isTeacher: z.boolean().optional(),
  isStaff: z.boolean().optional(),
  isCandidate: z.boolean().optional(),
  isAdmin: z.boolean().optional(),
  isAdvisor: z.boolean().optional(),
  isCoach: z.boolean().optional(),
  isAlumni: z.boolean().optional(),
  isBetaTester: z.boolean().optional(),
  isSkyApiSyncEnabled: z.boolean().optional()
})

/**
 * School user info returned with actor token
 */
export const ActorTokenSchoolUserInfoSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  preferredFirstName: z.string().nullable().optional(),
  preferredLastName: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  photoUrlThumbnail: z.string().nullable().optional(),
  photoUrlLarge: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  gradeLevel: z.string().nullable().optional(),
  roles: SchoolUserRolesSchema.optional(),
  lastSyncedAt: z.string().nullable().optional(),
  school: z
    .object({
      id: z.string(),
      name: z.string()
    })
    .nullable()
    .optional()
})

export const ActorTokenResponseSchema = z.object({
  data: z.object({
    actorToken: ClerkActorTokenSchema,
    schoolUserInfo: ActorTokenSchoolUserInfoSchema,
    // Self-scoped, single-use Clerk sign-in token minted for the REQUESTER so
    // they can end impersonation and return to their admin account without a
    // full re-login. `nullish` is deliberate: it tolerates an OLDER backend
    // (field absent) AND a fail-soft mint (null) — in both cases the mobile app
    // falls back to the existing soft-logout end path.
    adminReentry: z.object({ token: z.string(), expiresInSeconds: z.number() }).nullish()
  }),
  message: z.string()
})

export type ActorTokenRequest = z.infer<typeof ActorTokenRequestSchema>
export type ClerkActorToken = z.infer<typeof ClerkActorTokenSchema>
export type SchoolUserRoles = z.infer<typeof SchoolUserRolesSchema>
export type ActorTokenSchoolUserInfo = z.infer<typeof ActorTokenSchoolUserInfoSchema>
export type ActorTokenResponse = z.infer<typeof ActorTokenResponseSchema>

// =============================================================================
// GET /v1/auth/stream-chat/token
// =============================================================================

export const StreamChatTokenRequestSchema = z.object({})

export const StreamChatTokenResponseSchema = z.object({
  success: z.literal(true),
  token: z.string(),
  expires_at: z.string().datetime(),
  // The PUBLIC client key of the Stream app this token was signed for. Paired
  // atomically with `token` (both derived from the same resolved app) so the
  // mobile client points its Stream SDK at the matching app — a mismatch forces
  // a logout. `appId` is the Stream application id, informational for clients.
  apiKey: z.string(),
  appId: z.string().optional()
})

export type StreamChatTokenRequest = z.infer<typeof StreamChatTokenRequestSchema>
export type StreamChatTokenResponse = z.infer<typeof StreamChatTokenResponseSchema>

// =============================================================================
// GET /v1/auth/stream-video/token
// =============================================================================

// Query contract: `lifetime=native-push` opts into the long-lived (30d) token
// mobile's killed-app ringing path stores in secure storage; absent or unknown
// values normalize to the default (1h) variant — the clamp is fail-safe by
// design: a stale client never gets a 400, it gets a standard token.
export const StreamVideoTokenRequestSchema = z.object({
  lifetime: z.enum(['native-push']).optional().catch(undefined)
})

export const StreamVideoTokenResponseSchema = z.object({
  success: z.literal(true),
  token: z.string(),
  expires_at: z.string().datetime(),
  // The PUBLIC client key of the Stream app this Video token was signed for.
  // Paired atomically with `token` (both derived from the same resolved app —
  // Video is a product on the school's existing Stream app) so the client
  // points its Stream Video SDK at the matching app. `appId` is the Stream
  // application id, informational for clients.
  apiKey: z.string(),
  appId: z.string().optional()
})

export type StreamVideoTokenRequest = z.infer<typeof StreamVideoTokenRequestSchema>
export type StreamVideoTokenResponse = z.infer<typeof StreamVideoTokenResponseSchema>

// =============================================================================
// POST /v1/auth/livekit/token
// =============================================================================

export const LivekitTokenRequestSchema = z.object({
  roomName: z.string(),
  participantName: z.string().optional()
})

export const LivekitTokenResponseSchema = z.object({
  data: z.object({
    token: z.string(),
    serverUrl: z.string().optional()
  }),
  message: z.string()
})

export type LivekitTokenRequest = z.infer<typeof LivekitTokenRequestSchema>
export type LivekitTokenResponse = z.infer<typeof LivekitTokenResponseSchema>

// =============================================================================
// Legacy exports for backwards compatibility
// =============================================================================

/** @deprecated Use GetMeResponseSchema */
export const CurrentUserSchema = CurrentUserProfileSchema

/** @deprecated Use CurrentUserProfile */
export type CurrentUser = CurrentUserProfile

/** @deprecated Use GetMeResponseSchema */
export const GetUsersMeResponseSchema = GetMeResponseSchema

/** @deprecated Use GetMeResponse */
export type GetUsersMeResponse = GetMeResponse

// Audited internal agent impersonation. Expiry refers to the ticket, not a session JWT.
export const AgentImpersonationConfigSchema = z.object({
  clerkUserId: z.string().regex(/^user_[A-Za-z0-9]+$/)
})
export const AgentActorTokenRequestSchema = z.object({
  schoolUserPublicId: z.string().trim().min(1),
  reason: z.string().trim().min(1).max(500)
})
export const AgentActorTokenResponseSchema = z.object({
  data: z.object({
    token: z.string().min(1),
    tokenId: z.string().min(1),
    expiresAt: z.string().datetime(),
    expiresInSeconds: z.literal(3600)
  })
})
export type AgentActorTokenRequest = z.infer<typeof AgentActorTokenRequestSchema>
export type AgentActorTokenResponse = z.infer<typeof AgentActorTokenResponseSchema>
